import { Button } from '@/deploy/components/ui/Button';
import { Icon } from '@/deploy/components/ui/Icon';
import type { DeploymentStep, FileSource, StepType, TargetSummary } from '@/deploy/types/network';
import styles from './Network.module.scss';

const SOURCES: { value: FileSource; label: string }[] = [
  { value: 'FULLCHAIN', label: 'fullchain.pem (certificat + intermédiaires)' },
  { value: 'PRIVKEY', label: 'privkey.pem (clé privée)' },
  { value: 'CERT', label: 'cert.pem (certificat seul)' },
  { value: 'CHAIN', label: 'chain.pem (intermédiaires)' },
  { value: 'COMBINED', label: 'Clé + fullchain (haproxy)' },
];

interface StepsEditorProps {
  steps: DeploymentStep[];
  targets: TargetSummary[];
  onChange: (steps: DeploymentStep[]) => void;
  disabled?: boolean;
}

/** Procédure de déploiement d'un certificat externe : envois SCP et commandes, dans l'ordre. */
export function StepsEditor({ steps, targets, onChange, disabled }: StepsEditorProps) {
  const update = (i: number, patch: Partial<DeploymentStep>) =>
    onChange(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i: number, delta: number) => {
    const next = [...steps];
    const [s] = next.splice(i, 1);
    next.splice(i + delta, 0, s);
    onChange(next);
  };
  const add = (type: StepType) => {
    const targetId = steps[steps.length - 1]?.targetId ?? targets[0]?.id;
    if (targetId === undefined) return;
    onChange([...steps, type === 'UPLOAD'
      ? { targetId, type, source: steps.some((s) => s.source === 'FULLCHAIN') ? 'PRIVKEY' : 'FULLCHAIN', remotePath: '' }
      : { targetId, type, command: '' }]);
  };

  if (targets.length === 0) {
    return <div className={styles.info}>Aucun serveur externe déclaré. Un admin les ajoute dans <code>Paramètres &gt; Réseau</code>.</div>;
  }

  return (
    <div className={styles.inlineList}>
      {steps.length === 0 && <span className={styles.hint}>Aucune étape : le certificat est renouvelé mais déployé nulle part.</span>}
      {steps.map((s, i) => (
        <div key={i} className={styles.step}>
          <div className={styles.stepHead}>
            <span className={styles.stepIndex}>{i + 1}</span>
            <span>{s.type === 'UPLOAD' ? 'Envoi de fichier' : 'Commande'}</span>
            <span className={styles.spacer} />
            <Button variant="ghost" size="sm" disabled={disabled || i === 0} onClick={() => move(i, -1)} aria-label="Monter">↑</Button>
            <Button variant="ghost" size="sm" disabled={disabled || i === steps.length - 1} onClick={() => move(i, 1)} aria-label="Descendre">↓</Button>
            <Button variant="ghost" size="sm" disabled={disabled} leftIcon={<Icon name="x" size={12} />}
              onClick={() => onChange(steps.filter((_, j) => j !== i))} aria-label="Retirer" />
          </div>
          <div className={styles.formRow}>
            <label className={styles.field}>
              <span>Serveur</span>
              <select value={s.targetId} disabled={disabled} onChange={(e) => update(i, { targetId: Number(e.target.value) })}>
                {targets.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.username}@{t.host})</option>)}
              </select>
            </label>
            {s.type === 'UPLOAD' ? (
              <label className={styles.field}>
                <span>Fichier</span>
                <select value={s.source ?? 'FULLCHAIN'} disabled={disabled} onChange={(e) => update(i, { source: e.target.value as FileSource })}>
                  {SOURCES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
            ) : (
              <label className={styles.field}>
                <span>Timeout (s)</span>
                <input type="number" min={1} max={3600} value={s.timeoutSeconds ?? ''} placeholder="120" disabled={disabled}
                  onChange={(e) => update(i, { timeoutSeconds: e.target.value ? Number(e.target.value) : null })} />
              </label>
            )}
          </div>
          {s.type === 'UPLOAD' ? (
            <>
              <label className={styles.field}>
                <span>Chemin distant</span>
                <input value={s.remotePath ?? ''} placeholder="/etc/ssl/service/fullchain.pem" disabled={disabled}
                  onChange={(e) => update(i, { remotePath: e.target.value })} />
              </label>
              <div className={styles.formRow}>
                <label className={styles.field}>
                  <span>Droits</span>
                  <input value={s.fileMode ?? ''} disabled={disabled}
                    placeholder={s.source === 'PRIVKEY' || s.source === 'COMBINED' ? '600' : '644'}
                    onChange={(e) => update(i, { fileMode: e.target.value || null })} />
                </label>
                <label className={styles.field}>
                  <span>Propriétaire</span>
                  <input value={s.fileOwner ?? ''} placeholder="inchangé (ex : root:www-data)" disabled={disabled}
                    onChange={(e) => update(i, { fileOwner: e.target.value || null })} />
                </label>
              </div>
            </>
          ) : (
            <label className={styles.field}>
              <span>Commande</span>
              <textarea value={s.command ?? ''} placeholder="systemctl reload nginx" disabled={disabled}
                onChange={(e) => update(i, { command: e.target.value })} />
            </label>
          )}
          <label className={styles.checkbox}>
            <input type="checkbox" checked={!!s.continueOnError} disabled={disabled}
              onChange={(e) => update(i, { continueOnError: e.target.checked })} />
            Continuer si cette étape échoue
          </label>
        </div>
      ))}
      <div className={styles.toolbar}>
        <Button variant="ghost" size="sm" leftIcon={<Icon name="plus" size={12} />} disabled={disabled} onClick={() => add('UPLOAD')}>
          Envoi de fichier
        </Button>
        <Button variant="ghost" size="sm" leftIcon={<Icon name="plus" size={12} />} disabled={disabled} onClick={() => add('EXEC')}>
          Commande
        </Button>
      </div>
    </div>
  );
}

/** Etapes prêtes à envoyer (sans les champs de l'autre type). */
export function cleanSteps(steps: DeploymentStep[]): DeploymentStep[] {
  return steps.map((s) => (s.type === 'UPLOAD'
    ? { targetId: s.targetId, type: s.type, source: s.source, remotePath: s.remotePath?.trim(), fileMode: s.fileMode || null,
      fileOwner: s.fileOwner || null, continueOnError: !!s.continueOnError }
    : { targetId: s.targetId, type: s.type, command: s.command, timeoutSeconds: s.timeoutSeconds ?? null,
      continueOnError: !!s.continueOnError }));
}
